import * as TabsPrimitive from "@radix-ui/react-tabs";
import * as React from "react";

import { TabsIndicator } from "@/components/TabsIndicator";
import {
  RADIX_ACTIVE_TAB_SELECTOR,
  TABS_LIST_CLASS_NAME,
  TABS_TRIGGER_CLASS_NAME,
} from "@/constants/tabs";
import { cn } from "@/lib/utils";

const Tabs = TabsPrimitive.Root;

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, children, ...props }, ref) => {
  const listRef = React.useRef<HTMLDivElement | null>(null);

  // NOTE: The list needs its own handle for the sliding pill to measure, and a
  // caller's forwarded ref must keep working alongside it.
  const setListRef = React.useCallback(
    (node: HTMLDivElement | null): void => {
      listRef.current = node;
      if (typeof ref === "function") {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    },
    [ref],
  );

  return (
    <TabsPrimitive.List
      ref={setListRef}
      className={cn(
        // NOTE: NSAI-6259 — neutral track, brand-coloured active tab. This is
        // the same inversion the segmented toggle carried: `bg-muted` is
        // violet-tinted and the active tab was `bg-background`, so the tabs you
        // had NOT chosen wore the brand colour and the one you had wore none.
        TABS_LIST_CLASS_NAME,
        className,
      )}
      {...props}
    >
      <TabsIndicator
        listRef={listRef}
        activeTabSelector={RADIX_ACTIVE_TAB_SELECTOR}
      />
      {children}
    </TabsPrimitive.List>
  );
});
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(TABS_TRIGGER_CLASS_NAME, className)}
    {...props}
  />
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      "mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      className,
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsContent, TabsList, TabsTrigger };
